import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  Timestamp 
} from 'firebase/firestore';

const tournamentsRef = collection(db, 'tournaments');

export const tournamentService = {
  // トーナメント一覧の取得
  async getAllTournaments() {
    try {
      const snapshot = await getDocs(tournamentsRef);
      const data = snapshot.docs.map(doc => {
        const docData = doc.data();
        
        // Timestampを日付文字列に変換
        const createdAt = docData.createdAt?.toDate?.();
        const updatedAt = docData.updatedAt?.toDate?.();
        
        const processedData = {
          id: doc.id,
          ...docData,
          createdAt: createdAt ? createdAt.toISOString() : null,
          updatedAt: updatedAt ? updatedAt.toISOString() : null
        };

        // tournamentNameをtitleとして使用
        if (docData.tournamentName && !docData.title) {
          processedData.title = docData.tournamentName;
        }

        return processedData;
      });
      
      // 下書き（isDraft: true）を除外
      const publishedData = data.filter(doc => !doc.isDraft);
      
      return publishedData;
    } catch (error) {
      console.error('トーナメント一覧の取得に失敗:', error);
      throw error;
    }
  },

  // 下書き大会の取得
  async getDraftTournaments() {
    try {
      const q = query(tournamentsRef, where('isDraft', '==', true));
      const snapshot = await getDocs(q);
      let drafts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // importedAtでソート（降順）
      drafts.sort((a, b) => {
        const dateA = new Date(a.importedAt || 0);
        const dateB = new Date(b.importedAt || 0);
        return dateB - dateA;
      });
      
      return drafts;
    } catch (error) {
      console.error('下書き大会の取得に失敗:', error);
      throw error;
    }
  },

  // PandaScore IDで大会を検索
  async getTournamentByPandaScoreId(pandaScoreId) {
    try {
      const q = query(
        tournamentsRef,
        where('pandaScoreId', '==', pandaScoreId)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        return null;
      }
      
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('PandaScore IDでの大会検索に失敗:', error);
      throw error;
    }
  },

  // トーナメントの取得
  async getTournament(id) {
    try {
      const docRef = doc(tournamentsRef, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }
      return null;
    } catch (error) {
      console.error('トーナメントの取得に失敗:', error);
      throw error;
    }
  },

  // トーナメントの作成
  async createTournament(tournamentData) {
    try {
      // statusフィールドをisDraftに変換
      const { status, ...dataWithoutStatus } = tournamentData;
      const isDraft = status === 'draft';
      
      const docRef = await addDoc(tournamentsRef, {
        ...dataWithoutStatus,
        isDraft,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      console.error('トーナメントの作成に失敗:', error);
      throw error;
    }
  },

  // トーナメントの更新
  async updateTournament(id, tournamentData) {
    try {
      const docRef = doc(tournamentsRef, id);
      await updateDoc(docRef, {
        ...tournamentData,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('トーナメントの更新に失敗:', error);
      throw error;
    }
  },

  // トーナメントの削除
  async deleteTournament(id) {
    try {
      const docRef = doc(tournamentsRef, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('トーナメントの削除に失敗:', error);
      throw error;
    }
  },

  // 下書きを公開
  async publishDraft(id) {
    try {
      const docRef = doc(tournamentsRef, id);
      await updateDoc(docRef, {
        isDraft: false,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('下書きの公開に失敗:', error);
      throw error;
    }
  },

  // 月別のトーナメント取得
  async getTournamentsByMonth(year, month) {
    try {
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);
      
      const q = query(
        tournamentsRef,
        where('date', '>=', startDate.toISOString().split('T')[0]),
        where('date', '<=', endDate.toISOString().split('T')[0])
      );
      
      const snapshot = await getDocs(q);
      const tournaments = {};
      
      snapshot.forEach(doc => {
        const tournament = {
          id: doc.id,
          ...doc.data()
        };
        
        // 下書きは除外
        if (!tournament.isDraft) {
          const day = new Date(tournament.date).getDate();
          if (!tournaments[day]) {
            tournaments[day] = [];
          }
          tournaments[day].push(tournament);
        }
      });
      
      return tournaments;
    } catch (error) {
      console.error('月別トーナメントの取得に失敗:', error);
      throw error;
    }
  }
};
